(function () {
  'use strict';

  window.theme = window.theme || {};

  var CONFIG_SELECTOR = 'script[data-ultem-lid-v2-config]';
  var instances = {};

  function normalizeKey(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Where the two engravings sit on a set-of-two lid image. Slot 0 is the first
  // engraving field, slot 1 the second, and y is the vertical centre as a
  // percentage of the lid image box.
  //
  // The two lids are not symmetric about the middle of the image: it carries
  // more empty space below the lower lid than above the upper one, so the lid
  // centres land near 24 and 70 rather than 25 and 75. Nudge these to move an
  // engraving on its lid; one point is about one percent of the image height.
  //
  // Keys are handleized sizes with the "(Set of 2)" suffix stripped, because a
  // two-case lid is sold as one set: "Weekly AM-PM Pill Case (Set of 2)"
  // handleizes to weekly-am-pm-pill-case-set-of-2, which matches nothing in
  // theme.css.liquid either.
  var TWO_SIDE_LID_PREVIEW = {
    'weekly-am-pm-pill-case': [{ y: 74 }, { y: 30 }],
    'weekly-am-pm-vitamin-case': [{ y: 73 }, { y: 28 }],
    'weekly-am-pm-vitamin-xl-case': [{ y: 73 }, { y: 29 }],
    '2-week-pill-case': [{ y: 30 }, { y: 74 }],
    '2-week-vitamin-case': [{ y: 28 }, { y: 73 }],
    '2-week-vitamin-xl-case': [{ y: 29 }, { y: 73 }]
  };
  var DEFAULT_TWO_SIDE_SLOTS = {
    ampm: [{ y: 73 }, { y: 29 }],
    two_week: [{ y: 29 }, { y: 73 }]
  };
  var TWO_SIDE_FIELDS = {
    ampm: [
      { kind: 'left', label: 'AM / Left Side', property: 'AM - Left Side Lid Engraving', summary: 'AM' },
      { kind: 'right', label: 'PM / Right Side', property: 'PM - Right Side Lid Engraving', summary: 'PM' }
    ],
    two_week: [
      { kind: 'left', label: 'Week #1 - Top & Right Side', property: 'Week 1 - Top & Right Case #1 Lid Engraving', summary: 'Week 1' },
      { kind: 'right', label: 'Week #2 - Bottom & Left Side', property: 'Week 2 - Bottom & Left Case #2 Lid Engraving', summary: 'Week 2' }
    ]
  };
  function getFamily(source) {
    var variant = source && source.variant ? source.variant : source;
    var family = variant && variant.family ? String(variant.family) : '';
    return family === 'ampm' || family === 'two_week' ? family : 'single';
  }

  function isTwoSided(source) {
    return getFamily(source) !== 'single';
  }

  function getTwoSideFields(family) {
    return TWO_SIDE_FIELDS[family] || null;
  }

  // "Weekly AM-PM Pill Case (Set of 2)" and friends carry a set suffix that no
  // stylesheet rule knows about.
  function stripSetSuffix(sizeKey) {
    return String(sizeKey || '').replace(/-set-of-(?:\d+|two)$/, '');
  }

  // The single-case size a two-case lid should be styled as. Derived from the
  // size key rather than looked up, so it survives whatever else the merchant
  // appends to a size name.
  function getTwoSideStyleKey(sizeKey) {
    var key = stripSetSuffix(sizeKey);
    if (key.indexOf('vitamin-xl') !== -1 || key.indexOf('2xl') !== -1) return 'weekly-vitamin-xl-case';
    if (key.indexOf('vitamin') !== -1) return 'weekly-vitamin-case';
    if (key.indexOf('xs') !== -1) return 'weekly-xs-case';
    return 'weekly-pill-case';
  }

  function parseCents(value) {
    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function getEngravingLimit(selection) {
    var variant = selection && selection.variant ? selection.variant : selection;
    if (isTwoSided(variant)) return 12;
    var size = variant && variant.size ? String(variant.size).toLowerCase() : '';
    return size.indexOf('weekly') !== -1 ? 12 : 3;
  }

  function cleanEngraving(value, limit) {
    var maxLength = parseInt(limit, 10);
    if (!Number.isFinite(maxLength) || maxLength < 1) maxLength = 3;
    return String(value || '').trim().slice(0, maxLength);
  }

  function getEngravingValues(selection) {
    if (!selection) return [];
    var limit = getEngravingLimit(selection);
    if (isTwoSided(selection)) {
      return [cleanEngraving(selection.left, limit), cleanEngraving(selection.right, limit)].filter(Boolean);
    }
    var single = cleanEngraving(selection.single, limit);
    return single ? [single] : [];
  }

  function getEngravingIssue(variant, engravingProductConfigured) {
    if (!variant) return '';
    if (!engravingProductConfigured) {
      return 'Engraving is unavailable because the engraving product is not configured. You can still purchase this lid without engraving.';
    }
    if (parseInt(variant.engravingMatchCount, 10) === 0) {
      return 'Engraving is unavailable because this lid size has no exact engraving-service match. You can still purchase this lid without engraving.';
    }
    if (parseInt(variant.engravingMatchCount, 10) !== 1) {
      return 'Engraving is unavailable because this lid size has multiple engraving-service matches. You can still purchase this lid without engraving.';
    }
    if (!variant.engravingVariant || !variant.engravingVariant.id) {
      return 'Engraving is unavailable because its exact service variant could not be resolved. You can still purchase this lid without engraving.';
    }
    if (!variant.engravingVariant.available) {
      return 'Engraving is sold out for this lid size. You can still purchase this lid without engraving.';
    }
    return '';
  }

  function createBundleId() {
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function buildCartItems(selection) {
    selection = selection || {};
    var variant = selection.variant;
    if (!variant || !variant.id || !variant.available) {
      throw new Error('Choose an available lid size before adding to cart.');
    }

    var engravingValues = getEngravingValues(selection);
    var engravingIssue = getEngravingIssue(variant, selection.engravingProductConfigured !== false);
    if (engravingValues.length && engravingIssue) throw new Error(engravingIssue);

    var properties = {};
    var engravingLimit = getEngravingLimit(selection);
    var cartFields = getTwoSideFields(getFamily(selection));
    if (cartFields) {
      cartFields.forEach(function (field) {
        var value = cleanEngraving(selection[field.kind], engravingLimit);
        if (value) properties[field.property] = value;
      });
    } else {
      var single = cleanEngraving(selection.single, engravingLimit);
      if (single) properties['Lid Engraving'] = single;
    }

    var items = [];
    if (engravingValues.length) {
      var bundleId = String(selection.bundleId || createBundleId()).trim();
      if (!/^[A-Za-z0-9_-]{1,64}$/.test(bundleId)) {
        throw new Error('Could not create a valid cart bundle. Please try again.');
      }
      var bundleMarker = 'com2:' + bundleId;
      properties._ikg_bundle = bundleMarker;
      items.push({
        id: parseInt(variant.engravingVariant.id, 10) || variant.engravingVariant.id,
        quantity: engravingValues.length,
        properties: { _ikg_bundle: bundleMarker + ':lid' }
      });
    }
    items.push({
      id: parseInt(variant.id, 10) || variant.id,
      quantity: 1,
      properties: properties
    });
    return items;
  }

  function calculateTotalCents(selection) {
    selection = selection || {};
    var variant = selection.variant;
    if (!variant) return 0;
    var total = parseCents(variant.price);
    var engravingValues = getEngravingValues(selection);
    if (
      engravingValues.length &&
      !getEngravingIssue(variant, selection.engravingProductConfigured !== false)
    ) {
      total += parseCents(variant.engravingVariant.price) * engravingValues.length;
    }
    return total;
  }

  function parseConfig(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      if (window.console && typeof window.console.warn === 'function') {
        console.warn('[ultem-lid-v2] Could not parse section configuration.', error);
      }
      return null;
    }
  }

  function createInstance(cfg) {
    var root = document.getElementById('ultem-lid-v2-builder-' + cfg.sectionId);
    if (!root) return null;

    var STEP_ORDER = ['1', '2'];
    var variants = Array.isArray(cfg.variants) ? cfg.variants : [];
    var variantsById = {};
    var variantsBySizeKey = {};
    var catalogErrors = [];
    variants.forEach(function (variant) {
      if (!variant || !variant.id || !variant.size) {
        catalogErrors.push('A lid variant is missing its ID or exact size value.');
        return;
      }
      var id = String(variant.id);
      var sizeKey = normalizeKey(variant.sizeKey || variant.size);
      variant.sizeKey = sizeKey;
      if (variantsById[id]) catalogErrors.push('Duplicate lid variant ID: ' + id + '.');
      if (variantsBySizeKey[sizeKey]) catalogErrors.push('Duplicate lid size: ' + variant.size + '.');
      variantsById[id] = variant;
      variantsBySizeKey[sizeKey] = variant;
    });

    var fatalCatalogError = catalogErrors.join(' ');
    var sizeList = root.querySelector('[data-size-list]');
    var engravingFields = root.querySelector('[data-engraving-fields]');
    var engravingStatus = root.querySelector('[data-engraving-catalog-status]');
    var engravingHelp = root.querySelector('[data-engraving-help-text]');
    var priceEl = root.querySelector('[data-price]');
    var comparePriceEl = root.querySelector('[data-compare-price]');
    var stockPill = root.querySelector('[data-stock-pill]');
    var stockLabel = root.querySelector('[data-stock-label]');
    var catalogErrorEl = root.querySelector('[data-catalog-error]');
    var addButton = root.querySelector('[data-add-to-cart]');
    var addLabel = root.querySelector('[data-add-to-cart-label]');
    var addSpinner = root.querySelector('[data-add-to-cart-spinner]');
    var addError = root.querySelector('[data-add-to-cart-error]');
    var addMessage = root.querySelector('[data-add-to-cart-message]');
    var galleryStage = root.querySelector('[data-gallery-stage]');
    var galleryPrev = root.querySelector('[data-gallery-prev]');
    var galleryNext = root.querySelector('[data-gallery-next]');
    var mainSlide = root.querySelector('[data-gallery-slide][data-slide-index="0"]');
    var initialPrimaryMedia = root.querySelector('[data-initial-primary-media]');
    var mainComposite = root.querySelector('[data-primary-lid-composite]');
    var mainImage = root.querySelector('[data-primary-media]');
    var mainThumb = root.querySelector('[data-primary-thumb]');
    var initialPrimaryThumb = root.querySelector('[data-initial-primary-thumb]');
    var thumbComposite = root.querySelector('[data-primary-lid-thumb-composite]');
    var thumbImage = root.querySelector('[data-primary-thumb-image]');
    var engravingPreviewLayers = root.querySelectorAll('[data-lid-engraving-preview]');

    var state = {
      variant: null,
      single: '',
      left: '',
      right: '',
      touched: { '1': false, '2': false },
      expanded: '1'
    };
    var submitting = false;
    var destroyed = false;
    var activeGalleryIndex = 0;
    var swipeState = null;
    var openSizePopover = null;
    var popoverListenersBound = false;
    var tooltip = null;
    var tooltipTrigger = null;

    var FOCUS_RING_BLACK = 'tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-black tw:focus-visible:ring-offset-1';
    var FOCUS_RING_INSET = 'tw:focus:outline-none tw:focus-visible:bg-gray-50 tw:focus-visible:ring-2 tw:focus-visible:ring-inset tw:focus-visible:ring-gray-950';

    function getSelection() {
      return {
        variant: state.variant,
        family: getFamily(state.variant),
        single: state.single,
        left: state.left,
        right: state.right,
        engravingProductConfigured: cfg.engravingProductConfigured !== false
      };
    }

    function formatMoney(cents) {
      var amount = parseCents(cents);
      if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === 'function') {
        return window.theme.Currency.formatMoney(amount, window.theme.moneyFormat || cfg.moneyFormat);
      }
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(amount, cfg.moneyFormat);
      }
      return '$' + (amount / 100).toFixed(2);
    }

    function getStep(step) {
      return root.querySelector('[data-step="' + step + '"]');
    }

    function getStepHeader(step) {
      return root.querySelector('[data-step-header="' + step + '"]');
    }

    function getStepBody(step) {
      return root.querySelector('[data-step-body="' + step + '"]');
    }

    function getValidation(step) {
      return root.querySelector(step === '1' ? '[data-size-validation]' : '[data-engraving-validation]');
    }

    function setValidation(step, message) {
      var element = getValidation(step);
      if (!element) return;
      var text = String(message || '').trim();
      element.textContent = text;
      element.classList.toggle('tw:hidden', !text);
    }

    function setSummary(step, value) {
      var element = root.querySelector('[data-step-summary="' + step + '"]');
      if (!element) return;
      var text = String(value || '').trim();
      element.innerHTML = '';
      if (!text) {
        element.classList.add('tw:hidden');
        element.removeAttribute('data-summary-value');
        return;
      }
      var prefix = document.createElement('span');
      prefix.className = 'tw:font-normal tw:text-gray-600';
      prefix.textContent = 'Selected: ';
      var selected = document.createElement('span');
      selected.className = 'tw:font-bold tw:text-black';
      selected.textContent = text;
      element.appendChild(prefix);
      element.appendChild(selected);
      element.setAttribute('data-summary-value', text);
      element.setAttribute('aria-label', 'Selected: ' + text);
      element.classList.remove('tw:hidden');
    }

    function applyStepVisual(step) {
      var card = getStep(step);
      var header = getStepHeader(step);
      var body = getStepBody(step);
      if (!card || !header || !body) return;
      var locked = card.getAttribute('data-locked') === 'true';
      var active = state.expanded === step && !locked;
      var touched = !!state.touched[step];
      card.setAttribute('data-active', active ? 'true' : 'false');
      card.setAttribute('data-touched', touched ? 'true' : 'false');
      header.disabled = locked;
      header.setAttribute('aria-expanded', active ? 'true' : 'false');
      header.setAttribute('aria-disabled', locked ? 'true' : 'false');
      body.setAttribute('aria-hidden', active ? 'false' : 'true');
      body.inert = !active;
      body.classList.toggle('tw:grid-rows-[1fr]', active);
      body.classList.toggle('tw:grid-rows-[0fr]', !active);
      card.classList.toggle('tw:border-blue-500', active);
      card.classList.toggle('tw:border-green-500', !active && touched);
      card.classList.toggle('tw:border-gray-300', !active && !touched);
      header.classList.toggle('tw:bg-blue-50', active);
      header.classList.toggle('tw:bg-green-50', !active && touched);
      var chevron = header.querySelector('[data-step-chevron]');
      if (chevron) chevron.classList.toggle('tw:rotate-180', active);
    }

    function setLocked(step, locked) {
      var card = getStep(step);
      if (!card) return;
      card.setAttribute('data-locked', locked ? 'true' : 'false');
      if (locked && state.expanded === step) state.expanded = null;
      applyStepVisual(step);
    }

    function setTouched(step, touched) {
      state.touched[step] = !!touched;
      applyStepVisual(step);
    }

    function focusElementSoon(element) {
      if (!element || typeof element.focus !== 'function') return;
      var focus = function () {
        try {
          element.focus({ preventScroll: false });
        } catch (error) {
          element.focus();
        }
      };
      if (window.requestAnimationFrame) window.requestAnimationFrame(focus);
      else window.setTimeout(focus, 0);
    }

    function expandStep(step, options) {
      options = options || {};
      var card = getStep(step);
      if (!card || card.getAttribute('data-locked') === 'true') return;
      state.expanded = step;
      STEP_ORDER.forEach(applyStepVisual);
      if (options.focus) focusElementSoon(getStepHeader(step));
    }

    function setStock(status, label) {
      if (!stockPill) return;
      stockPill.setAttribute('data-stock-status', status);
      if (stockLabel) stockLabel.textContent = label;
    }

    function renderStock() {
      if (state.variant) {
        setStock(state.variant.available ? 'instock' : 'outstock', state.variant.available ? 'In stock' : 'Out of stock');
        return;
      }
      setStock(cfg.initialProductAvailable ? 'instock' : 'outstock', cfg.initialProductAvailable ? 'In stock' : 'Out of stock');
    }

    function calculateCompareTotalCents() {
      if (!state.variant) return 0;
      var base = parseCents(state.variant.price);
      var baseCompare = parseCents(state.variant.compareAtPrice);
      var total = baseCompare > base ? baseCompare : base;
      var values = getEngravingValues(getSelection());
      var issue = getEngravingIssue(state.variant, cfg.engravingProductConfigured !== false);
      if (values.length && !issue) {
        var addon = parseCents(state.variant.engravingVariant.price);
        var addonCompare = parseCents(state.variant.engravingVariant.compareAtPrice);
        total += (addonCompare > addon ? addonCompare : addon) * values.length;
      }
      return total;
    }

    function renderPrice() {
      if (!state.variant) {
        if (priceEl) priceEl.textContent = cfg.priceDescription || 'Select a Size to Show Price';
        if (comparePriceEl) comparePriceEl.classList.add('tw:hidden');
        return;
      }
      var total = calculateTotalCents(getSelection());
      if (priceEl) priceEl.textContent = formatMoney(total);
      if (comparePriceEl) {
        var compareTotal = calculateCompareTotalCents();
        var showCompare = compareTotal > total;
        comparePriceEl.textContent = showCompare ? formatMoney(compareTotal) : '';
        comparePriceEl.classList.toggle('tw:hidden', !showCompare);
      }
    }

    function ensureImage(parent, selector, attrs) {
      if (!parent) return null;
      var image = parent.querySelector(selector);
      if (image) return image;
      image = document.createElement('img');
      Object.keys(attrs || {}).forEach(function (name) { image.setAttribute(name, attrs[name]); });
      parent.appendChild(image);
      return image;
    }

    function updateProductMedia(variant) {
      if (!variant || !variant.featuredImage) return;
      var alt = cfg.productTitle + ' — ' + variant.size;
      var placeholder = mainSlide ? mainSlide.querySelector('[data-primary-placeholder]') : null;
      if (placeholder && placeholder.parentNode) placeholder.parentNode.removeChild(placeholder);
      mainImage = ensureImage(mainComposite, '[data-primary-media]', {
        'data-primary-media': '',
        'loading': 'eager',
        'width': '500',
        'height': '125'
      });
      thumbImage = ensureImage(thumbComposite, '[data-primary-thumb-image]', {
        'data-primary-thumb-image': '',
        'loading': 'lazy',
        'width': '500',
        'height': '125'
      });
      if (mainImage) {
        mainImage.src = variant.featuredImage;
        mainImage.alt = alt;
      }
      if (thumbImage) {
        thumbImage.src = variant.thumbnail || variant.featuredImage;
        thumbImage.alt = alt;
      }
      if (initialPrimaryMedia) initialPrimaryMedia.classList.add('tw:hidden');
      if (initialPrimaryThumb) initialPrimaryThumb.classList.add('tw:hidden');
      if (mainComposite) mainComposite.classList.remove('tw:hidden');
      if (thumbComposite) thumbComposite.classList.remove('tw:hidden');
      if (mainThumb) mainThumb.setAttribute('aria-label', alt);
      renderEngravingPreview();
      setActiveGallerySlide(0);
    }

    // The labels hang off a second .img-wrapper carrying the single-case size,
    // so theme.css.liquid gives them the exact font size, stroke and colour it
    // gives a Weekly Pill engraving, at every breakpoint and in the thumbnail.
    // Nothing about the engraving's size is duplicated here. Geometry is inline
    // because .imgWrapper .bundle-wrapper .img-wrapper outranks a class.
    function getPreviewMount(layer) {
      var mount = layer.querySelector('[data-lid-preview-slots]');
      if (mount) return mount;
      mount = document.createElement('span');
      mount.setAttribute('data-lid-preview-slots', '');
      mount.setAttribute('aria-hidden', 'true');
      mount.className = 'img-wrapper ultem';
      mount.style.position = 'absolute';
      mount.style.top = '0';
      mount.style.right = '0';
      mount.style.bottom = '0';
      mount.style.left = '0';
      mount.style.display = 'block';
      mount.style.pointerEvents = 'none';
      layer.appendChild(mount);
      return mount;
    }

    // A band across the lid image, centred on the slot, laid out exactly like
    // the ::after pseudo-element it stands in for. Position only — size, font,
    // colour and stroke all come from the mount.
    function createPreviewLabel(text, slot) {
      var label = document.createElement('span');
      label.style.position = 'absolute';
      label.style.left = '0';
      label.style.right = '0';
      label.style.top = (slot.y === undefined ? 50 : slot.y) + '%';
      label.style.transform = 'translateY(-50%)';
      label.style.display = 'flex';
      label.style.alignItems = 'center';
      label.style.justifyContent = 'center';
      label.textContent = text;
      return label;
    }

    // Single-sided sizes keep the CSS pseudo-element preview. Two-sided sizes
    // suppress it and place one label per lid instead, while still carrying
    // data-text / data-text1 so every per-size CSS font rule keeps matching.
    function renderEngravingPreview() {
      var selection = getSelection();
      var variant = selection.variant;
      var fields = getTwoSideFields(selection.family);
      var limit = getEngravingLimit(selection);
      var caseKey = variant ? variant.sizeKey : '';
      var styleKey = getTwoSideStyleKey(caseKey);
      var slots = TWO_SIDE_LID_PREVIEW[stripSetSuffix(caseKey)]
        || DEFAULT_TWO_SIDE_SLOTS[selection.family]
        || [];
      var texts = (fields || [{ kind: 'single' }]).map(function (field) {
        return variant ? String(state[field.kind] || '').slice(0, limit) : '';
      });

      [mainComposite, thumbComposite].forEach(function (composite) {
        if (composite) composite.classList.toggle('no-preview-engraving-text', !!fields);
      });

      for (var i = 0; i < engravingPreviewLayers.length; i++) {
        var layer = engravingPreviewLayers[i];
        var mount = getPreviewMount(layer);
        layer.setAttribute('data-case', caseKey);
        layer.setAttribute('data-text', texts[0]);
        layer.setAttribute('data-text1', fields ? texts[1] : '');
        mount.innerHTML = '';
        if (!fields) {
          mount.removeAttribute('data-case');
          continue;
        }

        // Drives the mount's own font size and stroke, and data-text keeps the
        // thumbnail rules that select on :not([data-text=""]) matching.
        mount.setAttribute('data-case', styleKey);
        mount.setAttribute('data-text', texts[0] || texts[1]);
        for (var slot = 0; slot < slots.length; slot++) {
          if (!texts[slot]) continue;
          mount.appendChild(createPreviewLabel(texts[slot], slots[slot]));
        }
      }
    }

    function setActiveGallerySlide(index) {
      var slides = root.querySelectorAll('[data-gallery-slide]');
      var thumbs = root.querySelectorAll('[data-gallery-thumb]');
      var count = slides.length;
      if (!count) return;
      var next = parseInt(index, 10);
      if (!Number.isFinite(next)) next = 0;
      next = ((next % count) + count) % count;
      activeGalleryIndex = next;
      for (var i = 0; i < slides.length; i++) {
        slides[i].setAttribute('data-active', String(parseInt(slides[i].getAttribute('data-slide-index'), 10) === next));
      }
      for (var j = 0; j < thumbs.length; j++) {
        var active = parseInt(thumbs[j].getAttribute('data-slide-target'), 10) === next;
        thumbs[j].setAttribute('data-active', active ? 'true' : 'false');
        thumbs[j].setAttribute('aria-current', active ? 'true' : 'false');
      }
      if (galleryPrev) galleryPrev.disabled = count <= 1;
      if (galleryNext) galleryNext.disabled = count <= 1;
    }

    function moveGallery(delta) {
      setActiveGallerySlide(activeGalleryIndex + delta);
    }

    function createSizeThumbnail(variant, sizeClass) {
      var thumbnail = document.createElement('span');
      thumbnail.className = 'tw:relative tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded tw:border tw:border-gray-200 tw:bg-gray-100 ' + (sizeClass || 'tw:h-10 tw:w-10');
      if (variant && variant.thumbnail) {
        var image = document.createElement('img');
        image.src = variant.thumbnail;
        image.alt = '';
        image.loading = 'lazy';
        image.className = 'tw:h-full tw:w-full tw:object-contain tw:p-0.5';
        thumbnail.appendChild(image);
      } else {
        var placeholder = document.createElement('span');
        placeholder.setAttribute('aria-hidden', 'true');
        placeholder.className = 'tw:flex tw:h-full tw:w-full tw:items-center tw:justify-center tw:text-gray-400';
        placeholder.innerHTML = '<svg class="tw:h-5 tw:w-5" viewBox="0 0 24 24" fill="none"><rect x="5" y="3" width="14" height="18" rx="3" stroke="currentColor" stroke-width="1.5"/><path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
        thumbnail.appendChild(placeholder);
      }
      if (variant && !variant.available) {
        var slash = document.createElement('span');
        slash.setAttribute('aria-hidden', 'true');
        slash.className = 'tw:pointer-events-none tw:absolute tw:left-1/2 tw:top-1/2 tw:h-0.5 tw:w-[140%] tw:-translate-x-1/2 tw:-translate-y-1/2 tw:-rotate-45 tw:bg-red-600';
        thumbnail.appendChild(slash);
      }
      return thumbnail;
    }

    function getPopoverTrigger(popover) {
      if (!popover || !popover.id) return null;
      return root.querySelector('[data-size-trigger][popovertarget="' + popover.id + '"]');
    }

    function placeSizePopover(popover) {
      var trigger = getPopoverTrigger(popover);
      if (!trigger) return;
      var rect = trigger.getBoundingClientRect();
      var viewportWidth = window.innerWidth || document.documentElement.clientWidth || rect.width;
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      var padding = 16;
      var width = Math.min(rect.width, Math.max(viewportWidth - padding * 2, 0));
      var left = Math.min(Math.max(rect.left, padding), Math.max(viewportWidth - width - padding, padding));
      popover.style.position = 'fixed';
      popover.style.margin = '0';
      popover.style.left = left + 'px';
      popover.style.right = 'auto';
      popover.style.bottom = 'auto';
      popover.style.width = width + 'px';
      var popupHeight = popover.offsetHeight || 0;
      var spaceBelow = viewportHeight - rect.bottom;
      popover.style.top = spaceBelow < popupHeight + padding && rect.top > popupHeight + padding
        ? (rect.top - popupHeight - 4) + 'px'
        : (rect.bottom + 4) + 'px';
    }

    function placeOpenSizePopover() {
      if (openSizePopover) placeSizePopover(openSizePopover);
    }

    function bindPopoverListeners() {
      if (popoverListenersBound) return;
      window.addEventListener('resize', placeOpenSizePopover);
      window.addEventListener('scroll', placeOpenSizePopover, true);
      popoverListenersBound = true;
    }

    function unbindPopoverListeners() {
      if (!popoverListenersBound) return;
      window.removeEventListener('resize', placeOpenSizePopover);
      window.removeEventListener('scroll', placeOpenSizePopover, true);
      popoverListenersBound = false;
    }

    function closeSizePopover(popover) {
      var target = popover || openSizePopover;
      if (!target) return;
      var trigger = getPopoverTrigger(target);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (typeof target.hidePopover === 'function') {
        try { target.hidePopover(); } catch (error) {}
      }
      if (openSizePopover === target) openSizePopover = null;
      unbindPopoverListeners();
    }

    function getEnabledSizeOptions(popover) {
      if (!popover) return [];
      return Array.prototype.slice.call(popover.querySelectorAll('[data-size-option-id]')).filter(function (option) {
        return !option.disabled && option.getAttribute('aria-disabled') !== 'true';
      });
    }

    function focusSizeOption(option) {
      if (!option) return;
      var popover = option.closest('[data-size-popover]');
      var options = popover ? popover.querySelectorAll('[data-size-option-id]') : [];
      for (var i = 0; i < options.length; i++) options[i].setAttribute('tabindex', options[i] === option ? '0' : '-1');
      focusElementSoon(option);
      if (typeof option.scrollIntoView === 'function') option.scrollIntoView({ block: 'nearest' });
    }

    function focusInitialSizeOption(popover, focusLast) {
      var options = getEnabledSizeOptions(popover);
      if (!options.length) return;
      var selected = options.find(function (option) { return option.getAttribute('aria-selected') === 'true'; });
      focusSizeOption(focusLast ? options[options.length - 1] : (selected || options[0]));
    }

    function openSizePopoverFromTrigger(trigger, focusLast) {
      if (!trigger) return;
      var popover = document.getElementById(trigger.getAttribute('popovertarget'));
      if (!popover) return;
      popover.setAttribute('data-focus-last-option', focusLast ? 'true' : 'false');
      placeSizePopover(popover);
      if (typeof popover.showPopover === 'function') {
        try { popover.showPopover(); return; } catch (error) {}
      }
      focusInitialSizeOption(popover, !!focusLast);
    }

    function onPopoverBeforeToggle(event) {
      if (event.newState === 'open') placeSizePopover(event.target);
    }

    function onPopoverToggle(event) {
      var trigger = getPopoverTrigger(event.target);
      if (event.newState === 'open') {
        openSizePopover = event.target;
        placeSizePopover(event.target);
        bindPopoverListeners();
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        var focusLast = event.target.getAttribute('data-focus-last-option') === 'true';
        event.target.removeAttribute('data-focus-last-option');
        focusInitialSizeOption(event.target, focusLast);
      } else {
        if (openSizePopover === event.target) openSizePopover = null;
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        unbindPopoverListeners();
      }
    }

    function renderSizeSelector() {
      if (!sizeList) return;
      var renderKey = (state.variant ? String(state.variant.id) : '') + '::' + fatalCatalogError;
      if (sizeList.getAttribute('data-render-key') === renderKey) return;
      if (openSizePopover && sizeList.contains(openSizePopover)) closeSizePopover(openSizePopover);
      sizeList.innerHTML = '';
      sizeList.setAttribute('data-render-key', renderKey);

      var selectedVariant = state.variant;
      var control = document.createElement('div');
      control.className = 'tw:flex tw:w-full tw:flex-col tw:gap-2';
      var popoverId = root.id + '-size-popover';
      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.setAttribute('data-size-trigger', '');
      trigger.setAttribute('popovertarget', popoverId);
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', popoverId);
      trigger.setAttribute('aria-label', 'Lid size: ' + (selectedVariant ? selectedVariant.size : 'Select lid size'));
      trigger.disabled = !!fatalCatalogError;
      trigger.className = 'tw:flex tw:w-full tw:min-h-12 tw:items-center tw:justify-between tw:gap-2 tw:rounded-md tw:border tw:border-gray-200 tw:bg-white tw:py-1.5 tw:pl-3 tw:pr-4 tw:text-left tw:cursor-pointer tw:transition-all tw:duration-fast tw:ease-standard tw:hover:border-gray-400 tw:disabled:cursor-not-allowed tw:disabled:opacity-50 ' + FOCUS_RING_BLACK;
      var triggerContent = document.createElement('span');
      triggerContent.className = 'tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-3';
      triggerContent.appendChild(createSizeThumbnail(selectedVariant, 'tw:h-11 tw:w-11'));
      var triggerName = document.createElement('span');
      triggerName.className = 'tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm tw:font-semibold tw:text-gray-950';
      triggerName.textContent = selectedVariant ? selectedVariant.size : 'Select lid size';
      triggerContent.appendChild(triggerName);
      trigger.appendChild(triggerContent);
      var chevron = document.createElement('span');
      chevron.setAttribute('aria-hidden', 'true');
      chevron.className = 'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:text-gray-500';
      chevron.innerHTML = '<svg class="tw:h-2 tw:w-3" viewBox="0 0 12 7" fill="none"><path d="M11 1L6 6L1 1" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
      trigger.appendChild(chevron);
      control.appendChild(trigger);

      var popover = document.createElement('div');
      popover.id = popoverId;
      popover.setAttribute('popover', 'auto');
      popover.setAttribute('data-size-popover', '');
      popover.setAttribute('role', 'listbox');
      popover.setAttribute('aria-label', 'Lid size');
      popover.className = 'tw:m-0 tw:border-0 tw:bg-transparent tw:p-0';
      var list = document.createElement('div');
      list.className = 'tw:max-h-80 tw:overflow-hidden tw:overflow-y-auto tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:p-0 tw:shadow-lg';
      variants.forEach(function (variant) {
        var selected = selectedVariant && String(selectedVariant.id) === String(variant.id);
        var option = document.createElement('button');
        option.type = 'button';
        option.setAttribute('data-size-option-id', variant.id);
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
        option.setAttribute('aria-disabled', variant.available ? 'false' : 'true');
        option.setAttribute('aria-label', variant.size + (variant.available ? '' : ', sold out') + (selected ? ', selected' : ''));
        option.setAttribute('tabindex', '-1');
        option.disabled = !variant.available;
        option.className = 'tw:flex tw:min-h-12 tw:w-full tw:items-center tw:gap-3 tw:border-0 tw:bg-white tw:px-3 tw:py-1.5 tw:text-left tw:cursor-pointer tw:transition-colors tw:duration-fast tw:ease-standard tw:hover:bg-gray-50 tw:disabled:cursor-not-allowed tw:disabled:opacity-40 ' + FOCUS_RING_INSET + (selected ? ' tw:bg-gray-50' : '');
        option.appendChild(createSizeThumbnail(variant, 'tw:h-10 tw:w-10'));
        var label = document.createElement('span');
        label.className = 'tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm tw:font-medium tw:text-gray-900';
        label.textContent = variant.size;
        option.appendChild(label);
        if (!variant.available) {
          var soldOut = document.createElement('span');
          soldOut.className = 'tw:rounded-full tw:bg-gray-100 tw:px-1.5 tw:py-0.5 tw:text-[10px] tw:font-semibold tw:leading-tight tw:text-gray-600';
          soldOut.textContent = 'Sold out';
          option.appendChild(soldOut);
        }
        var check = document.createElement('span');
        check.setAttribute('aria-hidden', 'true');
        check.className = 'tw:flex tw:h-4 tw:w-4 tw:flex-shrink-0 tw:items-center tw:justify-center tw:text-black' + (selected ? '' : ' tw:hidden');
        check.innerHTML = '<svg class="tw:h-4 tw:w-4" viewBox="0 0 16 16" fill="currentColor"><path d="M6.5 11.4 2.6 7.5 4 6.1l2.5 2.5L12 3.1l1.4 1.4z"></path></svg>';
        option.appendChild(check);
        list.appendChild(option);
      });
      popover.appendChild(list);
      popover.addEventListener('beforetoggle', onPopoverBeforeToggle);
      popover.addEventListener('toggle', onPopoverToggle);
      control.appendChild(popover);
      sizeList.appendChild(control);
    }

    function createEngravingField(kind, labelText) {
      var limit = getEngravingLimit(getSelection());
      var wrap = document.createElement('label');
      wrap.className = 'tw:flex tw:w-full tw:flex-col tw:gap-1.5 tw:mb-0';
      var label = document.createElement('span');
      label.className = 'tw:text-xs tw:font-bold tw:text-gray-950';
      label.textContent = labelText;
      wrap.appendChild(label);
      var inputWrap = document.createElement('span');
      inputWrap.className = 'tw:flex tw:w-full tw:items-center tw:gap-2';
      var input = document.createElement('input');
      input.type = 'text';
      input.maxLength = limit;
      input.autocomplete = 'off';
      input.placeholder = limit === 12
        ? 'Up to 12 characters'
        : (cfg.engravingPlaceholder || 'Up to 3 characters');
      input.setAttribute('data-engraving-field', kind);
      input.setAttribute('aria-label', labelText);
      input.className = 'tw:m-0 tw:min-h-11 tw:min-w-0 tw:flex-1 tw:rounded-md tw:border tw:border-gray-300 tw:bg-white tw:px-3 tw:py-2 tw:text-sm tw:text-gray-950 tw:transition-colors tw:duration-fast tw:ease-standard tw:hover:border-gray-400 tw:focus-visible:ring-black';
      input.value = state[kind] || '';
      inputWrap.appendChild(input);
      var count = document.createElement('span');
      count.setAttribute('data-character-count', kind);
      count.className = 'tw:w-10 tw:shrink-0 tw:text-right tw:text-xs tw:text-gray-500';
      count.textContent = String(input.value.length) + ' / ' + limit;
      inputWrap.appendChild(count);
      wrap.appendChild(inputWrap);
      return wrap;
    }

    function renderEngravingFields() {
      if (!engravingFields) return;
      var family = getFamily(state.variant);
      var renderKey = state.variant ? String(state.variant.id) + ':' + family : 'none';
      if (engravingFields.getAttribute('data-render-key') === renderKey) return;
      engravingFields.innerHTML = '';
      engravingFields.setAttribute('data-render-key', renderKey);
      if (!state.variant) return;
      var fields = getTwoSideFields(family);
      if (fields) {
        fields.forEach(function (field) {
          engravingFields.appendChild(createEngravingField(field.kind, field.label));
        });
        return;
      }
      engravingFields.appendChild(createEngravingField('single', 'Lid Engraving'));
    }

    function renderEngravingStatus() {
      if (!engravingStatus) return;
      var issue = state.variant ? getEngravingIssue(state.variant, cfg.engravingProductConfigured !== false) : '';
      engravingStatus.textContent = issue;
      engravingStatus.classList.toggle('tw:hidden', !issue);
      if (engravingHelp) {
        var limit = getEngravingLimit(getSelection());
        var copy = cfg.engravingHelpIsCustom
          ? cfg.engravingHelpCopy
          : 'Up to ' + limit + ' characters per lid. Leave blank for no engraving.';
        if (isTwoSided(state.variant)) copy += ' You may engrave either side or both sides.';
        engravingHelp.textContent = copy;
      }
    }

    function getEngravingSummary() {
      var selection = getSelection();
      var values = getEngravingValues(selection);
      if (!values.length) return 'No engraving';
      var fields = getTwoSideFields(getFamily(selection));
      if (!fields) return values[0];
      var limit = getEngravingLimit(selection);
      var parts = [];
      fields.forEach(function (field) {
        var value = cleanEngraving(selection[field.kind], limit);
        if (value) parts.push(field.summary + ': ' + value);
      });
      return parts.join(' · ');
    }

    function updateConfirmButtons() {
      var sizeConfirm = root.querySelector('[data-step-confirm="1"]');
      if (sizeConfirm) sizeConfirm.disabled = !!(fatalCatalogError || !state.variant || !state.variant.available);
      var engravingConfirm = root.querySelector('[data-step-confirm="2"]');
      var values = getEngravingValues(getSelection());
      var issue = state.variant ? getEngravingIssue(state.variant, cfg.engravingProductConfigured !== false) : '';
      if (engravingConfirm) engravingConfirm.disabled = !state.variant || !!(values.length && issue);
    }

    function isReady() {
      if (submitting || fatalCatalogError || !state.variant || !state.variant.available) return false;
      if (!state.touched['1'] || !state.touched['2']) return false;
      var values = getEngravingValues(getSelection());
      return !(values.length && getEngravingIssue(state.variant, cfg.engravingProductConfigured !== false));
    }

    function renderAddButton() {
      if (!addButton) return;
      var ready = isReady();
      addButton.disabled = !ready;
      if (addLabel) addLabel.textContent = ready ? 'Add to Cart' : 'Configure Your Lid First';
      if (!submitting && addSpinner) addSpinner.classList.add('tw:hidden');
    }

    function renderCatalogError() {
      if (!catalogErrorEl) return;
      catalogErrorEl.textContent = fatalCatalogError;
      catalogErrorEl.classList.toggle('tw:hidden', !fatalCatalogError);
    }

    function dispatchState() {
      root.dispatchEvent(new CustomEvent('ultem-lid-v2:statechange', {
        bubbles: true,
        detail: { state: state }
      }));
    }

    function refresh() {
      renderCatalogError();
      renderSizeSelector();
      renderEngravingFields();
      renderEngravingStatus();
      renderEngravingPreview();
      renderPrice();
      renderStock();
      updateConfirmButtons();
      renderAddButton();
      STEP_ORDER.forEach(applyStepVisual);
      dispatchState();
    }

    function resetForSize(variant) {
      state.variant = variant;
      state.single = '';
      state.left = '';
      state.right = '';
      state.touched['1'] = false;
      state.touched['2'] = false;
      state.expanded = '1';
      setSummary('1', '');
      setSummary('2', '');
      setValidation('1', '');
      setValidation('2', '');
      setLocked('1', false);
      setLocked('2', true);
      if (engravingFields) engravingFields.removeAttribute('data-render-key');
      updateProductMedia(variant);
      refresh();
    }

    function confirmStep(step, options) {
      options = options || {};
      if (step === '1') {
        if (fatalCatalogError || !state.variant || !state.variant.available) {
          setValidation('1', fatalCatalogError || 'Choose an available lid size before confirming.');
          return;
        }
        setValidation('1', '');
        setTouched('1', true);
        setSummary('1', state.variant.size);
        setLocked('2', false);
        expandStep('2', { focus: options.focus !== false });
        refresh();
        return;
      }

      var values = getEngravingValues(getSelection());
      var issue = state.variant ? getEngravingIssue(state.variant, cfg.engravingProductConfigured !== false) : '';
      if (values.length && issue) {
        setValidation('2', issue);
        return;
      }
      setValidation('2', '');
      setTouched('2', true);
      setSummary('2', getEngravingSummary());
      state.expanded = null;
      STEP_ORDER.forEach(applyStepVisual);
      refresh();
      if (options.focus !== false) focusElementSoon(addButton);
    }

    function setAddError(message) {
      var text = String(message || '').trim();
      if (addMessage) addMessage.textContent = text;
      if (addError) {
        addError.classList.toggle('tw:hidden', !text);
        addError.setAttribute('aria-hidden', text ? 'false' : 'true');
      }
    }

    function cartRoute(path) {
      var base = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
      return String(base).replace(/\/?$/, '/') + String(path || '').replace(/^\/+/, '');
    }

    function handleAddToCart() {
      if (!addButton || addButton.disabled || submitting) return;
      var items;
      try {
        items = buildCartItems(getSelection());
      } catch (error) {
        setAddError(error && error.message ? error.message : 'Could not build this cart selection.');
        return;
      }
      submitting = true;
      addButton.disabled = true;
      if (addLabel) addLabel.textContent = 'Adding...';
      if (addSpinner) addSpinner.classList.remove('tw:hidden');
      setAddError('');
      if (window.console && typeof window.console.log === 'function') {
        console.log('[ultem-lid-v2] cart payload', items);
      }
      fetch(cartRoute('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) {
          if (response.ok) return response.json();
          return response.json().catch(function () { return {}; }).then(function (payload) {
            throw new Error(payload.description || payload.message || 'Could not add this item to cart.');
          });
        })
        .then(function () { window.location.href = cartRoute('cart'); })
        .catch(function (error) {
          submitting = false;
          if (addSpinner) addSpinner.classList.add('tw:hidden');
          setAddError(error && error.message ? error.message : 'Could not add this item to cart.');
          refresh();
        });
    }

    function closeTooltip() {
      if (tooltip && tooltip.parentNode) tooltip.parentNode.removeChild(tooltip);
      tooltip = null;
      tooltipTrigger = null;
    }

    function openTooltip(trigger) {
      var url = trigger && trigger.getAttribute('data-tooltip');
      if (!url) return;
      if (tooltipTrigger === trigger) {
        closeTooltip();
        return;
      }
      closeTooltip();
      tooltip = document.createElement('div');
      tooltip.className = 'dynamic-tooltip';
      tooltip.style.backgroundImage = 'url("' + String(url).replace(/"/g, '\\"') + '")';
      tooltip.style.position = 'absolute';
      tooltip.style.display = 'none';
      document.body.appendChild(tooltip);
      tooltipTrigger = trigger;
      var activeTooltip = tooltip;
      var viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      var isMobile = viewportWidth <= 768;
      var imageWidth = isMobile ? viewportWidth * 0.85 : 750;
      var sourceImage = new Image();
      sourceImage.onload = function () {
        if (tooltip !== activeTooltip) return;
        var naturalWidth = sourceImage.naturalWidth || sourceImage.width || imageWidth;
        var naturalHeight = sourceImage.naturalHeight || sourceImage.height || imageWidth;
        if (!isMobile && naturalWidth < imageWidth) imageWidth = naturalWidth;
        var rect = trigger.getBoundingClientRect();
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        activeTooltip.style.width = imageWidth + 'px';
        activeTooltip.style.height = (imageWidth / naturalWidth) * naturalHeight + 'px';
        activeTooltip.style.top = rect.top + scrollTop + 20 + 'px';
        activeTooltip.style.left = Math.max((viewportWidth - imageWidth) / 2, 0) + 'px';
        activeTooltip.style.display = 'block';
      };
      sourceImage.onerror = function () { if (tooltip === activeTooltip) closeTooltip(); };
      sourceImage.src = url;
    }

    function selectVariant(variant) {
      if (!variant || !variant.available) return;
      if (state.variant && String(state.variant.id) === String(variant.id)) return;
      resetForSize(variant);
    }

    function onRootClick(event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var thumb = target.closest('[data-gallery-thumb]');
      if (thumb && root.contains(thumb)) {
        event.preventDefault();
        setActiveGallerySlide(thumb.getAttribute('data-slide-target'));
        return;
      }
      if (target.closest('[data-gallery-prev]')) {
        event.preventDefault();
        moveGallery(-1);
        return;
      }
      if (target.closest('[data-gallery-next]')) {
        event.preventDefault();
        moveGallery(1);
        return;
      }
      var tooltipControl = target.closest('[data-compare-tooltip]');
      if (tooltipControl && root.contains(tooltipControl)) {
        event.preventDefault();
        event.stopPropagation();
        openTooltip(tooltipControl);
        return;
      }
      var sizeOption = target.closest('[data-size-option-id]');
      if (sizeOption && root.contains(sizeOption)) {
        event.preventDefault();
        if (sizeOption.disabled || sizeOption.getAttribute('aria-disabled') === 'true') return;
        var variant = variantsById[String(sizeOption.getAttribute('data-size-option-id'))];
        closeSizePopover(sizeOption.closest('[data-size-popover]'));
        selectVariant(variant);
        focusElementSoon(root.querySelector('[data-size-trigger]'));
        return;
      }
      var confirm = target.closest('[data-step-confirm]');
      if (confirm && root.contains(confirm)) {
        event.preventDefault();
        confirmStep(confirm.getAttribute('data-step-confirm'));
        return;
      }
      var header = target.closest('[data-step-header]');
      if (header && root.contains(header)) {
        event.preventDefault();
        var step = header.getAttribute('data-step-header');
        if (header.disabled) return;
        state.expanded = state.expanded === step ? null : step;
        STEP_ORDER.forEach(applyStepVisual);
        return;
      }
      if (target.closest('[data-add-to-cart]')) {
        event.preventDefault();
        handleAddToCart();
      }
    }

    function onRootInput(event) {
      var input = event.target && event.target.closest ? event.target.closest('[data-engraving-field]') : null;
      if (!input || !root.contains(input)) return;
      var kind = input.getAttribute('data-engraving-field');
      if (kind !== 'single' && kind !== 'left' && kind !== 'right') return;
      var limit = getEngravingLimit(getSelection());
      state[kind] = String(input.value || '').slice(0, limit);
      if (input.value !== state[kind]) input.value = state[kind];
      var count = root.querySelector('[data-character-count="' + kind + '"]');
      if (count) count.textContent = String(state[kind].length) + ' / ' + limit;
      if (state.touched['2']) {
        setTouched('2', false);
        setSummary('2', '');
      }
      var values = getEngravingValues(getSelection());
      var issue = state.variant ? getEngravingIssue(state.variant, cfg.engravingProductConfigured !== false) : '';
      setValidation('2', values.length && issue ? issue : '');
      renderEngravingPreview();
      renderPrice();
      updateConfirmButtons();
      renderAddButton();
      dispatchState();
    }

    function onRootKeydown(event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      var tooltipControl = target.closest('[data-compare-tooltip]');
      if (tooltipControl && root.contains(tooltipControl) && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        event.stopPropagation();
        openTooltip(tooltipControl);
        return;
      }
      var sizeOption = target.closest('[data-size-option-id]');
      if (sizeOption && root.contains(sizeOption)) {
        var popover = sizeOption.closest('[data-size-popover]');
        var options = getEnabledSizeOptions(popover);
        var currentIndex = options.indexOf(sizeOption);
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          event.preventDefault();
          if (options.length) focusSizeOption(options[(currentIndex + 1 + options.length) % options.length]);
          return;
        }
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
          event.preventDefault();
          if (options.length) focusSizeOption(options[(currentIndex - 1 + options.length) % options.length]);
          return;
        }
        if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          if (options.length) focusSizeOption(event.key === 'Home' ? options[0] : options[options.length - 1]);
          return;
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          var trigger = getPopoverTrigger(popover);
          closeSizePopover(popover);
          focusElementSoon(trigger);
          return;
        }
      }
      var sizeTrigger = target.closest('[data-size-trigger]');
      if (sizeTrigger && root.contains(sizeTrigger) && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        openSizePopoverFromTrigger(sizeTrigger, event.key === 'ArrowUp');
        return;
      }
      if (target.closest('[data-gallery-stage]')) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          moveGallery(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          moveGallery(1);
        }
      }
    }

    function onPointerDown(event) {
      swipeState = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }

    function onPointerUp(event) {
      if (!swipeState || swipeState.id !== event.pointerId) return;
      var dx = event.clientX - swipeState.x;
      var dy = event.clientY - swipeState.y;
      swipeState = null;
      if (Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy) * 1.2) moveGallery(dx < 0 ? 1 : -1);
    }

    function onPointerCancel() {
      swipeState = null;
    }

    function onDocumentClick(event) {
      var target = event.target;
      if (tooltip && (!target || typeof target.closest !== 'function' || !target.closest('[data-compare-tooltip], .dynamic-tooltip'))) {
        closeTooltip();
      }
    }

    root.addEventListener('click', onRootClick);
    root.addEventListener('input', onRootInput);
    root.addEventListener('keydown', onRootKeydown);
    document.addEventListener('click', onDocumentClick);
    if (galleryStage) {
      galleryStage.addEventListener('pointerdown', onPointerDown);
      galleryStage.addEventListener('pointerup', onPointerUp);
      galleryStage.addEventListener('pointercancel', onPointerCancel);
    }

    STEP_ORDER.forEach(function (step) {
      setLocked(step, step !== '1');
      setTouched(step, false);
    });
    setActiveGallerySlide(0);
    renderCatalogError();
    renderSizeSelector();
    renderEngravingFields();
    expandStep('1');

    if (cfg.selectedVariantId && variantsById[String(cfg.selectedVariantId)]) {
      selectVariant(variantsById[String(cfg.selectedVariantId)]);
    } else {
      refresh();
    }

    return {
      root: root,
      state: state,
      buildCartItems: function () { return buildCartItems(getSelection()); },
      destroy: function () {
        destroyed = true;
        closeTooltip();
        closeSizePopover(openSizePopover);
        unbindPopoverListeners();
        root.removeEventListener('click', onRootClick);
        root.removeEventListener('input', onRootInput);
        root.removeEventListener('keydown', onRootKeydown);
        document.removeEventListener('click', onDocumentClick);
        if (galleryStage) {
          galleryStage.removeEventListener('pointerdown', onPointerDown);
          galleryStage.removeEventListener('pointerup', onPointerUp);
          galleryStage.removeEventListener('pointercancel', onPointerCancel);
        }
      }
    };
  }

  function init(cfg) {
    if (!cfg || cfg.sectionId === null || cfg.sectionId === undefined) return null;
    var sectionId = String(cfg.sectionId);
    var root = document.getElementById('ultem-lid-v2-builder-' + sectionId);
    if (!root) return null;
    if (instances[sectionId] && instances[sectionId].root === root) return instances[sectionId];
    if (instances[sectionId]) instances[sectionId].destroy();
    cfg.sectionId = sectionId;
    instances[sectionId] = createInstance(cfg);
    return instances[sectionId];
  }

  function initFrom(scope) {
    var context = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    if (!context || typeof context.querySelectorAll !== 'function') return;
    var configs = [];
    if (context.matches && context.matches(CONFIG_SELECTOR)) configs.push(context);
    var descendants = context.querySelectorAll(CONFIG_SELECTOR);
    for (var i = 0; i < descendants.length; i++) configs.push(descendants[i]);
    for (var j = 0; j < configs.length; j++) {
      var cfg = parseConfig(configs[j]);
      if (cfg) init(cfg);
    }
  }

  function destroy(sectionId) {
    var key = String(sectionId || '');
    if (!instances[key]) return;
    instances[key].destroy();
    delete instances[key];
  }

  var publicApi = {
    normalizeKey: normalizeKey,
    stripSetSuffix: stripSetSuffix,
    getTwoSideStyleKey: getTwoSideStyleKey,
    getEngravingLimit: getEngravingLimit,
    getEngravingValues: getEngravingValues,
    getEngravingIssue: getEngravingIssue,
    calculateTotalCents: calculateTotalCents,
    buildCartItems: buildCartItems,
    init: init,
    initFrom: initFrom,
    destroy: destroy
  };
  window.theme.ultemLidV2 = publicApi;

  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('shopify:section:load', function (event) { initFrom(event.target); });
    document.addEventListener('shopify:section:unload', function (event) {
      if (event.detail && event.detail.sectionId) destroy(event.detail.sectionId);
    });
    function boot() { initFrom(document); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
    else boot();
  }
})();
